import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, useSortable, rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  DotsSixVertical, ArrowUp, ArrowDown, FileText, Warning, Pulse,
  Plus, ArrowRight, CurrencyDollar, Users,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import api from "@/lib/api";

const COLORS = ["#38BDF8", "#10B981", "#FBBF24", "#F43F5E", "#A855F7"];

function money(v, ccy = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(v || 0);
}

function KPIWidget({ label, value, hint, trend = 0, icon: Icon }) {
  return (
    <GlassCard className="p-6 h-full">
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        {Icon && <Icon size={18} weight="duotone" className="text-[var(--text-3)]" />}
      </div>
      <div className="mt-4 text-3xl md:text-4xl font-semibold tracking-tighter" style={{ fontFamily: 'var(--font-heading)' }}>
        {value}
      </div>
      {hint && (
        <div className="mt-2 text-xs text-[var(--text-3)] flex items-center gap-1">
          {trend > 0 ? <ArrowUp size={12} className="text-emerald-400" /> : trend < 0 ? <ArrowDown size={12} className="text-rose-400" /> : null}
          {hint}
        </div>
      )}
    </GlassCard>
  );
}

function SortableWidget({ id, children, onToggle, enabled }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : 1,
  };
  if (!enabled) return null;
  return (
    <div ref={setNodeRef} style={style} className="relative group">
      <button
        {...attributes} {...listeners}
        aria-label="Drag to reorder"
        data-testid={`drag-handle-${id}`}
        className="absolute top-3 end-3 z-10 opacity-0 group-hover:opacity-100 transition p-1.5 rounded-md bg-[var(--surface-strong)] border border-[var(--border)] cursor-grab active:cursor-grabbing"
      >
        <DotsSixVertical size={14} />
      </button>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [obligations, setObligations] = useState([]);
  const [layout, setLayout] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/dashboard"),
      api.get("/contracts"),
      api.get("/obligations"),
      api.get("/dashboard/layout"),
    ]).then(([a, c, o, l]) => {
      setData(a.data);
      setContracts(c.data);
      setObligations(o.data);
      setLayout(l.data);
    });
  }, []);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const widgets = layout.widgets;
    const oldIdx = widgets.findIndex((w) => w.id === active.id);
    const newIdx = widgets.findIndex((w) => w.id === over.id);
    const newWidgets = arrayMove(widgets, oldIdx, newIdx);
    const newLayout = { ...layout, widgets: newWidgets };
    setLayout(newLayout);
    api.patch("/dashboard/layout", { widgets: newWidgets });
  };

  const toggleWidget = (id) => {
    const newWidgets = layout.widgets.map((w) => w.id === id ? { ...w, enabled: !w.enabled } : w);
    setLayout({ ...layout, widgets: newWidgets });
    api.patch("/dashboard/layout", { widgets: newWidgets });
  };

  const widgetMap = useMemo(() => {
    const kpis = data?.kpis || {};
    return ({
    "kpi-total": { span: "md:col-span-3", node: <KPIWidget label="Total contracts" value={kpis.total_contracts ?? "—"} hint={`${kpis.active_contracts ?? 0} active`} icon={FileText} /> },
    "kpi-value": { span: "md:col-span-3", node: <KPIWidget label="Portfolio value" value={money(kpis.total_value, "USD")} hint="across all currencies" icon={CurrencyDollar} /> },
    "kpi-risk": { span: "md:col-span-3", node: <KPIWidget label="High-risk contracts" value={kpis.high_risk_contracts ?? 0} hint="needs attention" icon={Warning} /> },
    "kpi-breach": { span: "md:col-span-3", node: <KPIWidget label="At-risk obligations" value={kpis.at_risk_obligations ?? 0} hint="monitoring" icon={Pulse} /> },
    "chart-monthly": {
      span: "md:col-span-8",
      node: (
        <GlassCard className="p-6 h-full">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Contract velocity</div>
              <div className="text-lg font-medium" style={{ fontFamily: 'var(--font-heading)' }}>Last 6 months</div>
            </div>
          </div>
          <div className="h-64">
            <div data-testid="chart-container" style={{width:"100%",height:"100%"}}><ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.monthly_volume || []}>
                <defs>
                  <linearGradient id="gradArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#38BDF8" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#38BDF8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="month" stroke="var(--text-3)" fontSize={11} />
                <YAxis stroke="var(--text-3)" fontSize={11} />
                <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-strong)", borderRadius: 8 }} />
                <Area type="monotone" dataKey="contracts" stroke="#38BDF8" fill="url(#gradArea)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer></div>
          </div>
        </GlassCard>
      ),
    },
    "chart-risk": {
      span: "md:col-span-4",
      node: (
        <GlassCard className="p-6 h-full">
          <div className="overline mb-4">Risk distribution</div>
          <div className="h-56">
            <div data-testid="chart-container" style={{width:"100%",height:"100%"}}><ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data?.risk_distribution || []} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                  {(data?.risk_distribution || []).map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                </Pie>
                <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-strong)", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer></div>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {(data?.risk_distribution || []).map((r, i) => (
              <div key={r.name} className="flex items-center gap-1.5 text-xs">
                <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="capitalize">{r.name}</span>
                <span className="text-[var(--text-3)]">{r.value}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      ),
    },
    "chart-status": {
      span: "md:col-span-6",
      node: (
        <GlassCard className="p-6 h-full">
          <div className="overline mb-4">Pipeline status</div>
          <div className="h-56">
            <div data-testid="chart-container" style={{width:"100%",height:"100%"}}><ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.status_distribution || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" stroke="var(--text-3)" fontSize={11} />
                <YAxis stroke="var(--text-3)" fontSize={11} />
                <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-strong)", borderRadius: 8 }} />
                <Bar dataKey="value" fill="#10B981" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer></div>
          </div>
        </GlassCard>
      ),
    },
    "list-obligations": {
      span: "md:col-span-6",
      node: (
        <GlassCard className="p-6 h-full">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Upcoming obligations</div>
            <Button size="sm" variant="ghost" onClick={() => nav("/app/monitoring")} data-testid="see-all-obligations-btn">
              See all <ArrowRight size={12} />
            </Button>
          </div>
          <div className="space-y-2">
            {obligations.slice(0, 5).map((o) => (
              <div key={o.id} className="flex items-center gap-3 p-2.5 rounded-md hover:bg-[var(--surface-2)] transition">
                <span className={`chip chip-${o.priority === "critical" ? "critical" : o.priority === "high" ? "high" : o.priority === "medium" ? "medium" : "low"}`}>{o.priority}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{o.title}</div>
                  <div className="text-xs text-[var(--text-3)]">{o.owner} · {o.due_date ? new Date(o.due_date).toLocaleDateString() : "TBD"}</div>
                </div>
                <span className={`chip chip-${o.status === "breached" ? "critical" : o.status === "at_risk" ? "high" : o.status === "on_track" ? "low" : "neutral"}`}>{o.status?.replace("_"," ")}</span>
              </div>
            ))}
            {obligations.length === 0 && <div className="text-sm text-[var(--text-3)] text-center py-8">No obligations yet.</div>}
          </div>
        </GlassCard>
      ),
    },
    "list-contracts": {
      span: "md:col-span-6",
      node: (
        <GlassCard className="p-6 h-full">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Recent contracts</div>
            <Button size="sm" variant="ghost" onClick={() => nav("/app/repository")} data-testid="see-all-contracts-btn">
              See all <ArrowRight size={12} />
            </Button>
          </div>
          <div className="space-y-2">
            {contracts.slice(0, 5).map((c) => (
              <button
                key={c.id}
                data-testid={`contract-row-${c.id}`}
                onClick={() => nav(`/app/repository/${c.id}`)}
                className="w-full text-left flex items-center gap-3 p-2.5 rounded-md hover:bg-[var(--surface-2)] transition"
              >
                <FileText size={16} className="text-[var(--text-3)] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.title}</div>
                  <div className="text-xs text-[var(--text-3)] truncate">{c.counterparty} · {money(c.value_amount, c.value_currency)}</div>
                </div>
                <span className={`chip chip-${c.risk_level || "unknown"}`}>{c.risk_level || "—"}</span>
              </button>
            ))}
          </div>
        </GlassCard>
      ),
    },
    });
  }, [data, contracts, obligations, nav]);

  if (!data || !layout) {
    return (
      <PageShell title="Dashboard" subtitle="Welcome">
        <div className="animate-pulse space-y-4">
          <div className="h-32 glass" />
          <div className="h-64 glass" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Dashboard"
      subtitle="Overview"
      right={
        <>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="customize-dashboard-btn" className="glass-subtle border-[var(--border-strong)]">
                Customize
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-3 glass-strong border-0">
              <div className="overline mb-2">Show widgets</div>
              <div className="space-y-1">
                {layout.widgets.map((w) => (
                  <label key={w.id} className="flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-[var(--surface-2)] text-sm cursor-pointer">
                    <span>{w.title}</span>
                    <Switch checked={w.enabled} onCheckedChange={() => toggleWidget(w.id)} data-testid={`toggle-widget-${w.id}`} />
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <Button onClick={() => nav("/app/studio")} data-testid="new-contract-btn" className="gap-2">
            <Plus size={14} /> New contract
          </Button>
        </>
      }
    >
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="mb-5">
          <OnboardingChecklist />
        </div>
        <SortableContext items={layout.widgets.map((w) => w.id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
            <AnimatePresence>
              {layout.widgets.filter((w) => w.enabled).map((w, idx) => (
                <motion.div
                  key={w.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04, duration: 0.4 }}
                  className={`col-span-1 ${widgetMap[w.id]?.span || "md:col-span-6"}`}
                >
                  <SortableWidget id={w.id} enabled={w.enabled}>
                    {widgetMap[w.id]?.node}
                  </SortableWidget>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </SortableContext>
      </DndContext>
    </PageShell>
  );
}
